/* eslint-disable class-methods-use-this */

/**
 * API de la section « Intervenants » (annuaire / fiche / sous-ressources).
 * Suit le même pattern que les autres modules api (fetch + Bearer token).
 */
class IntervenantApi {
  get base() {
    return `${process.env.REACT_APP_API_URL}/api/intervenants`;
  }

  headers(accessToken) {
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    };
  }

  async post(path, body, accessToken) {
    const resp = await fetch(`${this.base}${path}`, {
      method: "POST",
      body: JSON.stringify(body || {}),
      headers: this.headers(accessToken),
    });
    return resp.json();
  }

  async get(path, accessToken) {
    const resp = await fetch(`${this.base}${path}`, {
      method: "GET",
      headers: this.headers(accessToken),
    });
    return resp.json();
  }

  /* ---------------- Annuaire ---------------- */

  async fetchIntervenants(accessToken, filters = {}) {
    const json = await this.post("", filters, accessToken);
    return { data: json.data || [], counts: json.counts || {} };
  }

  async getIntervenant(id, accessToken) {
    const json = await this.get(`/${id}`, accessToken);
    return json.data || null;
  }

  /* ---------------- CRUD ---------------- */

  async createIntervenant(payload, accessToken) {
    return this.post("/create", payload, accessToken);
  }

  async updateIntervenant(payload, accessToken) {
    return this.post("/update", payload, accessToken);
  }

  /* ---------------- Espace personnel de l'intervenant ---------------- */

  /**
   * État de l'espace : compte existant, statut, invitation en attente.
   * Ne renvoie jamais de mot de passe ni de jeton.
   */
  async getSpaceStatus(id, accessToken) {
    const json = await this.post("/space/status", { id }, accessToken);
    return json.data || null;
  }

  /**
   * (Re)envoie le lien d'invitation. Répond toujours 200 : le refus métier
   * (pas d'email, adresse déjà utilisée...) se lit dans `space.ok` / `space.code`.
   */
  async inviteSpace(id, accessToken) {
    return this.post("/space/invite", { id }, accessToken);
  }

  /** Factures et frais saisis par le formateur depuis son espace (onglet Suivi OF). */
  async getSpaceFactures(id, accessToken) {
    const json = await this.post("/space/factures", { id }, accessToken);
    return json.data || [];
  }

  /** Conflits de planning à venir (même calcul que l'espace formateur). */
  async getSpaceConflicts(id, accessToken) {
    const json = await this.post("/space/conflicts", { id }, accessToken);
    return json.data || [];
  }

  /** Disponibilités datées à venir, déclarées dans le planning de l'espace. */
  async getSpaceAvailability(id, accessToken) {
    const json = await this.post("/space/availability", { id }, accessToken);
    return json.data || [];
  }

  /* ---------------- Finances (onglet Finances de la fiche) ---------------- */

  /** Vue consolidée des rémunérations — la même que « Mes finances » du formateur. Renvoie la réponse complète. */
  async getSpaceFinances(id, accessToken) {
    const json = await this.post("/space/finances", { id }, accessToken);
    return json.data || null;
  }

  /** Crée ou met à jour une prestation A, B, B1, D ou E. Répond avec la vue à jour. */
  async saveFinanceMission(payload, accessToken) {
    return this.post("/finances/mission", payload, accessToken);
  }

  async deleteFinanceMission(intervenantId, id, accessToken) {
    return this.post("/finances/mission/delete", { intervenant_id: intervenantId, id }, accessToken);
  }

  /** { intervenant_id, kind: facture|mission, id, status }. Répond avec la vue à jour. */
  async setFinanceStatus(payload, accessToken) {
    return this.post("/finances/status", payload, accessToken);
  }

  /* ---------------- Conflits & incidents (onglet Suivi OF) ---------------- */

  /** { types, incidents } — tickets déclarés depuis l'espace formateur. */
  async getSpaceIncidents(id, accessToken) {
    const json = await this.post("/space/incidents", { id }, accessToken);
    return json.data || null;
  }

  /** { intervenant_id, id, status, response } — répond avec la liste à jour. */
  async updateSpaceIncident(payload, accessToken) {
    return this.post("/space/incidents/update", payload, accessToken);
  }

  /* ---------------- Veille (Qualiopi 22) et CPP ---------------- */

  /** Action ajoutée par l'OF, avec son axe CPP. */
  async addVeilleEntry(payload, accessToken) {
    return this.post("/space/veille/add", payload, accessToken);
  }

  /** { intervenant_id, id, axe } — classe une action dans un axe de la certification périodique. */
  async setVeilleAxe(payload, accessToken) {
    return this.post("/space/veille/axe", payload, accessToken);
  }

  /** Coupe l'accès et ferme les sessions ouvertes. Le compte n'est pas supprimé. */
  async disableSpace(id, accessToken) {
    return this.post("/space/disable", { id }, accessToken);
  }

  async deleteIntervenant(id, accessToken) {
    return this.post("/delete", { id }, accessToken);
  }

  async restoreIntervenant(id, accessToken) {
    return this.post("/restore", { id }, accessToken);
  }

  /* ---------------- Import CV (PDF → IA) ---------------- */

  async importCv(pdfBase64, accessToken) {
    return this.post("/import-cv", { pdf_base64: pdfBase64 }, accessToken);
  }

  /* ---------------- Sessions ---------------- */

  async linkSession(payload, accessToken) {
    return this.post("/link-session", payload, accessToken);
  }

  async unlinkSession(linkId, intervenantId, accessToken) {
    return this.post("/unlink-session", { linkId, intervenantId }, accessToken);
  }

  /** Intervenants rattachés à une session (formateur principal en tête). */
  async getBySession(sessionId, accessToken) {
    const json = await this.get(`/session/${sessionId}`, accessToken);
    return json && Array.isArray(json.data) ? json.data : [];
  }

  /* ---------------- Documents ---------------- */

  async addDocument(payload, accessToken) {
    return this.post("/documents/add", payload, accessToken);
  }

  async validateDocument(id, accessToken) {
    return this.post("/documents/validate", { id }, accessToken);
  }

  async deleteDocument(id, accessToken) {
    return this.post("/documents/delete", { id }, accessToken);
  }

  /* ---------------- Conformité Qualiopi ---------------- */

  async saveCompetences(intervenantId, competences, accessToken) {
    return this.post("/competences/save", { intervenant_id: intervenantId, competences }, accessToken);
  }

  /** Matrice + historique seuls : actualise l'onglet Qualiopi sans recharger toute la fiche. */
  async getCompetenceState(id, accessToken) {
    const json = await this.post("/competences/state", { id }, accessToken);
    return json.data || null;
  }

  async getCompetenceSnapshot(id, accessToken) {
    const json = await this.get(`/competences/snapshot/${id}`, accessToken);
    return json.data || null;
  }

  async addEntretien(payload, accessToken) {
    return this.post("/entretiens/add", payload, accessToken);
  }

  async addVeille(payload, accessToken) {
    return this.post("/veille/add", payload, accessToken);
  }

  async validateVeille(id, accessToken) {
    return this.post("/veille/validate", { id }, accessToken);
  }

  /* ---------------- Suivi OF ---------------- */

  async addTache(payload, accessToken) {
    return this.post("/taches/add", payload, accessToken);
  }

  async toggleTache(id, done, accessToken) {
    return this.post("/taches/toggle", { id, done }, accessToken);
  }

  async deleteTache(id, accessToken) {
    return this.post("/taches/delete", { id }, accessToken);
  }

  async addEchange(payload, accessToken) {
    return this.post("/echanges/add", payload, accessToken);
  }

  async deleteEchange(id, accessToken) {
    return this.post("/echanges/delete", { id }, accessToken);
  }

  async addIncident(payload, accessToken) {
    return this.post("/incidents/add", payload, accessToken);
  }
}

export const intervenantApi = new IntervenantApi();
